import React, { useEffect, useState } from 'react'

import { Button, Col, Form, Modal, Row } from 'react-bootstrap'
import { Formik, FormikHelpers } from 'formik'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import { AttendanceTypeResponse } from '../api/registration.types'
import { createAttendance, getAttendanceTypeList } from '../api/registration'
import dayjs from 'dayjs'
import DatePicker from '../components/DatePicker'

interface Values {
  attendance: {
    dateFrom: string
    dateTo: string
    attendanceType: string
    comment: string
  }[]
  choosePeriod: boolean
}

interface Props {
  id: string
  placementDateFrom: string
  placementDateTo: string
  onClose: (created: boolean) => void
}

const PlacementAttendanceModal: React.FC<Props> = ({ id, onClose, placementDateFrom, placementDateTo }) => {
  const { t } = useTranslation(['placementAttendance', 'common'])
  const [loading, setLoading] = useState(false)
  const [attendanceTypes, setAttendanceTypes] = useState<AttendanceTypeResponse[] | null>(null)

  const loadAttendanceTypes = async () => {
    setLoading(true)
    try {
      const response = await getAttendanceTypeList(id)

      setAttendanceTypes(response.data)
    } catch {
      toast.error(t('common:kerfisvilla'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAttendanceTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Formik<Values>
        initialValues={{
          attendance: [
            {
              dateTo: dayjs().add(1, 'day').format('DD.MM.YYYY'),
              dateFrom: dayjs().format('DD.MM.YYYY'),
              attendanceType: '',
              comment: '',
            },
          ],
          choosePeriod: false,
        }}
        // validationSchema={validationSchema}
        enableReinitialize
        onSubmit={async (values: Values, { setSubmitting, setFieldError }: FormikHelpers<Values>) => {
          try {
            const responses = []
            let data = {
              date_from: '',
              date_to: '',
              attendance_type: '',
              comment: '',
            }

            for (const s of values.attendance) {
              if (values.choosePeriod) {
                data = {
                  date_from: dayjs(s.dateFrom, 'DD.MM.YYYY').format('YYYY-MM-DD'),
                  date_to: dayjs(s.dateTo, 'DD.MM.YYYY').format('YYYY-MM-DD'),
                  attendance_type: s.attendanceType,
                  comment: s.comment,
                }
              } else {
                data = {
                  date_from: dayjs(s.dateFrom, 'DD.MM.YYYY').format('YYYY-MM-DD'),
                  date_to: dayjs(s.dateFrom, 'DD.MM.YYYY').format('YYYY-MM-DD'),
                  attendance_type: s.attendanceType,
                  comment: s.comment,
                }
              }

              const response = await createAttendance(id, data)

              responses.push(response.data)
            }
            toast.success(t('attendanceCreated'))

            onClose(true)
          } catch (e) {
            toast.error(t('villaVidAdSkra'))
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({ handleReset, handleSubmit, setStatus, getFieldProps, values, setFieldValue }) => (
          <Modal show onHide={() => onClose(true)} backdrop="static" keyboard={false} size="xl">
            <Modal.Header closeButton>
              <Modal.Title>{t('tilkynnaforfoll')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form
                noValidate
                onReset={handleReset}
                onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
                  setStatus('submitted')
                  handleSubmit(e)
                }}
              >
                <Form.Check
                  id="choosePeriod"
                  type="checkbox"
                  className="mb-2"
                  label={t('skraTimabil')}
                  custom
                  {...getFieldProps('choosePeriod')}
                />
                {values.attendance.map((_, idx) => (
                  <div key={`attendance.${idx}`}>
                    <Form.Group controlId={`attendance.${idx}`}>
                      <Row>
                        {!values.choosePeriod && (
                          <Col md={4}>
                            <Form.Label>{t('dagsetning')}</Form.Label>
                            <DatePicker
                              dayPickerProps={{
                                disabledDays: [
                                  { before: new Date(placementDateFrom), after: new Date(placementDateTo) },
                                ],
                              }}
                              onDayChange={(day: Date) => {
                                setFieldValue(`attendance.${idx}.dateFrom`, dayjs(day).format('DD.MM.YYYY'))
                              }}
                              {...getFieldProps(`attendance.${idx}.dateFrom`)}
                            />
                          </Col>
                        )}
                        {values.choosePeriod && (
                          <>
                            <Col md={2}>
                              <Form.Label>{t('dagsetningFra')}</Form.Label>
                              <DatePicker
                                dayPickerProps={{
                                  disabledDays: [
                                    { before: new Date(placementDateFrom), after: new Date(placementDateTo) },
                                  ],
                                }}
                                onDayChange={(day: Date) => {
                                  setFieldValue(`attendance.${idx}.dateFrom`, dayjs(day).format('DD.MM.YYYY'))
                                }}
                                {...getFieldProps(`attendance.${idx}.dateFrom`)}
                              />
                            </Col>
                            <Col md={2}>
                              <Form.Label>{t('dagsetningTil')}</Form.Label>
                              <DatePicker
                                dayPickerProps={{
                                  disabledDays: [
                                    { before: new Date(placementDateFrom), after: new Date(placementDateTo) },
                                  ],
                                }}
                                onDayChange={(day: Date) => {
                                  setFieldValue(
                                    `attendance.${idx}.dateTo`,
                                    dayjs(day).add(1, 'day').format('DD.MM.YYYY')
                                  )
                                }}
                                {...getFieldProps(`attendance.${idx}.dateTo`)}
                              />
                            </Col>
                          </>
                        )}
                        <Col md={4}>
                          <Form.Label>{t('fjarvist')}</Form.Label>
                          <Form.Control as="select" {...getFieldProps(`attendance.${idx}.attendanceType`)}>
                            <option value="">{t('velduFjarvistartegund')}</option>
                            {attendanceTypes &&
                              attendanceTypes.map((attendanceType) => (
                                <option key={attendanceType.id} value={attendanceType.id}>
                                  {attendanceType.name}
                                </option>
                              ))}
                          </Form.Control>
                          {!loading && attendanceTypes?.length === 0 && (
                            <div className="invalid-feedback d-block text-left">{t('enginnFjarvistartegund')}</div>
                          )}
                        </Col>
                        <Col md={4}>
                          <Form.Label>{t('athugasemd')}</Form.Label>
                          <Form.Control type="name" {...getFieldProps(`attendance.${idx}.comment`)} />
                        </Col>{' '}
                      </Row>
                    </Form.Group>
                  </div>
                ))}
              </Form>

              {!values.choosePeriod && (
                <Button
                  type="button"
                  variant="secondary"
                  className="float-right"
                  onClick={(e) =>
                    setFieldValue('attendance', [
                      ...values.attendance,
                      {
                        date: dayjs().add(1, 'day').format('DD.MM.YYYY'),
                        attendanceType: '',
                        comment: '',
                      },
                    ])
                  }
                >
                  <FontAwesomeIcon icon={faPlusCircle} /> {t('baetaviddagsetningu')}
                </Button>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="primary"
                type="button"
                onClick={() => {
                  setStatus('submitted')
                  handleSubmit()
                }}
              >
                {t('submit')}
              </Button>
              <Button variant="secondary" onClick={() => onClose(false)}>
                {t('cancel')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
    </>
  )
}

export default PlacementAttendanceModal
